import { invertRamp, linearRamp } from './palette';
import type { DesignTheme, PaletteRamp } from './types';
import { DEFAULT_OPACITY_BASES } from './types';

/**
 * Brand colours from `public/logo-light.svg` / `public/logo-dark.svg`:
 * - Gradient accent: `#1C39F5` → `#00FCFF`
 * - Wordmark (light logo): `#1F1F1F` · wordmark (dark logo): `#FFFFFF`
 */

/** Logo gradient — electric blue (start). */
const BRAND_BLUE = '#1C39F5';
/** Logo gradient — cyan (end). */
const BRAND_CYAN = '#00FCFF';
/** Logo wordmark on light backgrounds. */
const BRAND_INK = '#1F1F1F';

/** Primary ramp anchored on logo blue (`step 50` → `--color-primary`). */
const brandPrimaryLight: PaletteRamp = {
  10: '#eef6ff',
  20: '#d4e8ff',
  30: '#9ee8ff',
  40: '#52b8ff',
  50: BRAND_BLUE,
  60: '#1734d9',
  70: '#132cb5',
  80: '#0f2491',
  90: '#0b1b6d',
  100: '#071347',
};

/** Dark UI primary — brighter blues/cyan for contrast on charcoal surfaces. */
const brandPrimaryDark: PaletteRamp = {
  10: '#060e2e',
  20: '#0a1848',
  30: '#0f2266',
  40: '#15318a',
  50: '#4d6fff',
  60: '#6b87ff',
  70: '#8aa0ff',
  80: '#adc0ff',
  90: '#d0ddff',
  100: '#eef4ff',
};

/** Secondary ramp anchored on logo cyan (`--color-secondary`, accents). */
const brandSecondaryLight: PaletteRamp = {
  10: '#e8feff',
  20: '#c5faff',
  30: '#8ff5ff',
  40: '#52f0ff',
  50: BRAND_CYAN,
  60: '#00d9e6',
  70: '#00b8c4',
  80: '#0097a3',
  90: '#007682',
  100: '#005561',
};

const brandSecondaryDark: PaletteRamp = {
  10: '#002a30',
  20: '#003d46',
  30: '#005058',
  40: '#006a74',
  50: '#00d4e0',
  60: '#00e8f5',
  70: '#33f0ff',
  80: '#66f5ff',
  90: '#99f9ff',
  100: '#e6feff',
};

/**
 * Default design tokens — single source for colour, glass, and shared UI chrome (`ui`).
 * Consumed by `themeToCssString` in `lib/theme/css.ts` (injected in `src/app/layout.tsx`).
 * Tailwind palette names map to the same ramps in `styles/base/variables.css` @theme.
 *
 * Aesthetic: TechnoConix logo blue→cyan gradient, cool charcoal neutrals (`#1F1F1F`),
 * layered dark surfaces, frosted `glass`, and `--ui-*` control metrics.
 */
export const defaultTheme: DesignTheme = {
  opacityBases: DEFAULT_OPACITY_BASES,
  primitives: {
    primary: {
      light: brandPrimaryLight,
      dark: brandPrimaryDark,
    },
    secondary: {
      light: brandSecondaryLight,
      dark: brandSecondaryDark,
    },
    neutral: {
      light: linearRamp('#f6f7f9', BRAND_INK),
      dark: invertRamp(linearRamp('#f6f7f9', BRAND_INK)),
    },
    error: {
      light: linearRamp('#fff1f0', '#4a0b0b'),
      dark: invertRamp(linearRamp('#fff1f0', '#4a0b0b')),
    },
    warning: {
      light: linearRamp('#fff9ed', '#3d2800'),
      dark: invertRamp(linearRamp('#fff9ed', '#3d2800')),
    },
    success: {
      light: linearRamp('#f0fdf4', '#042f18'),
      dark: invertRamp(linearRamp('#f0fdf4', '#042f18')),
    },
    info: {
      light: linearRamp('#e8feff', '#004d55'),
      dark: invertRamp(linearRamp('#e8feff', '#004d55')),
    },
  },
  state: {
    light: {
      hover: '#1734d9',
      pressed: '#122bb5',
      focus: '#e8f2ff',
      focusBorder: BRAND_BLUE,
      disabled: '#c5c9d4',
      selected: 'rgba(28, 57, 245, 0.12)',
      hoverOverlay: 'rgba(28, 57, 245, 0.06)',
      pressedOverlay: 'rgba(28, 57, 245, 0.1)',
      disabledText: '#8b909c',
      disabledBg: '#f0f2f6',
      focusRing: BRAND_BLUE,
    },
    dark: {
      hover: '#00e8f5',
      pressed: '#00c4d0',
      focus: '#252a36',
      focusBorder: BRAND_CYAN,
      disabled: '#4a4f5c',
      selected: 'rgba(0, 252, 255, 0.18)',
      hoverOverlay: 'rgba(0, 252, 255, 0.08)',
      pressedOverlay: 'rgba(0, 252, 255, 0.14)',
      disabledText: '#6b7080',
      disabledBg: '#2a2e38',
      focusRing: BRAND_CYAN,
    },
  },
  semantic: {
    light: {
      bg: {
        primary: '#f4f6fa',
        secondary: '#ffffff',
        tertiary: '#e6eaf2',
        inverse: BRAND_INK,
      },
      text: {
        primary: BRAND_INK,
        secondary: '#5c6270',
        disabled: '#a3a8b3',
        inverse: '#ffffff',
      },
      border: {
        default: '#d4dae6',
        subtle: '#e6eaf2',
        strong: '#b8bfcc',
      },
      icon: { primary: BRAND_INK },
    },
    dark: {
      bg: {
        primary: BRAND_INK,
        secondary: '#2a2d35',
        tertiary: '#363a45',
        inverse: '#ffffff',
      },
      text: {
        primary: '#f5f7fa',
        secondary: '#a8adb8',
        disabled: '#6b7080',
        inverse: BRAND_INK,
      },
      border: {
        default: '#3d424f',
        subtle: '#2a2d35',
        strong: '#525866',
      },
      icon: { primary: '#f5f7fa' },
    },
  },
  glass: {
    light: {
      fill: 'rgba(255, 255, 255, 0.64)',
      fillElevated: 'rgba(255, 255, 255, 0.8)',
      stroke: 'rgba(28, 57, 245, 0.08)',
      highlight: 'rgba(255, 255, 255, 0.6)',
      shade: 'rgba(28, 57, 245, 0.04)',
      blur: '22px',
      saturate: '180%',
    },
    dark: {
      fill: 'rgba(31, 31, 31, 0.72)',
      fillElevated: 'rgba(42, 45, 53, 0.82)',
      stroke: 'rgba(0, 252, 255, 0.12)',
      highlight: 'rgba(0, 252, 255, 0.08)',
      shade: 'rgba(0, 0, 0, 0.4)',
      blur: '28px',
      saturate: '200%',
    },
  },
  /** Radius, motion, control metrics, breakpoints → `--ui-*` / `--bp-*` in `themeToCssString` (same in light/dark). */
  ui: {
    radius: {
      xs: '0.25rem',
      sm: '0.375rem',
      md: '0.5rem',
      lg: '0.75rem',
      xl: '1rem',
      full: '9999px',
    },
    motion: {
      durationXs: '120ms',
      durationSm: '200ms',
      durationMd: '300ms',
      durationLg: '450ms',
      durationXl: '500ms',
      easingStandard: 'cubic-bezier(0.4, 0, 0.2, 1)',
      easingEmphasized: 'cubic-bezier(0.2, 0, 0, 1)',
      easingDecelerate: 'cubic-bezier(0, 0, 0.2, 1)',
    },
    control: {
      paddingX: '0.75rem',
      paddingY: '0.5rem',
      minHeightTouch: '44px',
      borderWidth: '1px',
      labelFontSize: '0.75rem',
      labelLineHeight: '1.25',
      labelFontWeight: '500',
      fontSize: '0.875rem',
    },
    /**
     * `--bp-*` + Tailwind `xs:` … `4xl:` — watch uses `watchMax` with max-width queries in `styles/base/responsive.css`.
     * Align fallbacks in `styles/base/variables.css` `:root` when changing defaults.
     */
    breakpoints: {
      watchMax: '239px',
      xs: '475px',
      sm: '640px',
      md: '768px',
      lg: '1024px',
      xl: '1280px',
      twoXl: '1536px',
      threeXl: '1920px',
      fourXl: '2560px',
    },
  },
};
